import * as React from "react";

import type { SpecType } from "@/api/contracts/specifications";
import type { ItemDialogSeed } from "@/components/catalog/item-detail-dialog";
import { CircularProgress } from "@/components/ui/circular-progress";
import { EmptyTable } from "@/components/ui/empty-table";
import { ScrollToTopButton } from "@/components/ui/scroll-to-top-button";
import {
  GROUP_EXPAND_COLLAPSE_MS,
  useDeepLinkSearch,
  useSpecScrollRegion,
  type SpecGroupExpansion,
  type SpecLinesLoader,
} from "@/hooks/spec-lines";
import type { SpecLinesLine } from "@/hooks/specifications";
import { MUI_UP_MD_QUERY, useMatchesMedia } from "@/hooks/use-matches-media";

import { SpecGroup, SpecGroupSkeleton, SpecGroupsSkeletonPair } from "./spec-group";
import { SpecLineRows } from "./spec-line-rows";

// The details page's lines body — everything the donor's island draws beneath
// its header container (`SpecDetailsIsland.tsx:214-305`), over the loader it
// reads (`shared/data/loader.ts`) (#695). The Editor mounts the same loader and
// body with its own rows (`renderLine`).
//
// Top to bottom, each an item of the frame's scroll region, 16px apart:
//
//   - the **search skeleton** while a new term's first page is on its way (the
//     old results are hidden meanwhile);
//   - the deep link's **overlay** while it looks for its line;
//   - **No results**, for a version with no groups or a search with no match;
//   - the **groups**, up to the display cursor, each with its lines;
//   - the **pagination skeleton** while there are more lines, which loads the
//     next page as it comes within a viewport of the region's bottom;
//
// and, floating, the scroll-to-top button. The load-error snackbar is the
// page's, which raises one for the summary read and the lines alike.

/** The pagination observer's settle — the group collapse's own duration, so
 *  a folding group never triggers a fetch (`Skeletons.tsx:41-60`). */
export const PAGINATION_SETTLE_MS = GROUP_EXPAND_COLLAPSE_MS;

export interface SpecLinesBodyProps {
  loader: SpecLinesLoader;
  expansion: SpecGroupExpansion;
  specType: SpecType;
  showChanges: boolean;
  showTargetCost: boolean;
  /** `?lineId=` and `?selectionId=`. */
  lineId?: number;
  selectionId?: number;
  onOpenItem: (item: ItemDialogSeed) => void;
  /** The Editor's rows in place of the details page's. */
  renderLine?: (line: SpecLinesLine, context: { isDesktop: boolean }) => React.ReactNode;
}

export function SpecLinesBody({
  loader,
  expansion,
  specType,
  showChanges,
  showTargetCost,
  lineId,
  selectionId,
  onOpenItem,
  renderLine,
}: SpecLinesBodyProps) {
  const region = useSpecScrollRegion();
  const isDesktop = useMatchesMedia(MUI_UP_MD_QUERY);
  const looking = useDeepLinkSearch(lineId, loader, expansion);

  const highlightSelectionId =
    lineId !== undefined && selectionId !== undefined ? selectionId : null;

  return (
    <>
      {loader.isSearching ? <SpecGroupsSkeletonPair testId="spec-lines-search-skeleton" /> : null}

      {looking ? (
        <div
          data-testid="spec-lines-deep-link-overlay"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/30"
        >
          <CircularProgress size={60} />
        </div>
      ) : null}

      {loader.isNoResults ? <EmptyTable text="No results" /> : null}

      <div data-testid="groups-grid" className="flex flex-col gap-4">
        {(loader.ordering ?? []).map((entry, index) =>
          index > loader.displayCursor ? null : (
            <SpecGroup
              key={entry.group.id}
              group={entry.group}
              expansion={expansion}
              showChanges={showChanges}
              showTargetCost={showTargetCost}
              isDesktop={isDesktop}
              loadedLineCount={entry.lines.length}
              // The group flag's header: the details page's, with the switch
              // on, at `md` and up; the Editor's card draws its own
              // (`editable-spec-group.tsx`, #1104).
              baseChanges={
                showChanges && isDesktop && !renderLine
                  ? { specType, group: entry.group }
                  : undefined
              }
            >
              {entry.lines.map((line) =>
                renderLine ? (
                  <React.Fragment key={line.id}>{renderLine(line, { isDesktop })}</React.Fragment>
                ) : (
                  <SpecLineRows
                    key={line.id}
                    line={line}
                    specType={specType}
                    showChanges={showChanges}
                    highlightSelectionId={highlightSelectionId}
                    isDesktop={isDesktop}
                    onOpenItem={onOpenItem}
                  />
                ),
              )}
            </SpecGroup>
          ),
        )}
      </div>

      {loader.showPaginationSkeleton ? (
        <PaginationSkeleton root={region} onLoadMore={loader.loadMore} />
      ) : null}

      <ScrollToTopButton target={region} />
    </>
  );
}

/**
 * `LinePaginationSkeleton` (`Skeletons.tsx:41-67`): two group skeletons that
 * load the next page once they come within a viewport of the region's bottom —
 * an `IntersectionObserver` rooted at the scroll region, `rootMargin '100%
 * 0px'`, then the 300ms settle. Rebuilt whenever the callback changes, so it
 * fires again while it stays in range; unmounted after an error, so nothing
 * re-arms it.
 */
function PaginationSkeleton({
  root,
  onLoadMore,
}: {
  root: HTMLElement | null;
  onLoadMore: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const target = ref.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          window.setTimeout(onLoadMore, PAGINATION_SETTLE_MS);
        }
      },
      { root, rootMargin: "100% 0px", threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [root, onLoadMore]);

  return <SpecGroupsSkeletonPair ref={ref} testId="spec-lines-pagination-skeleton" />;
}

/**
 * The five group skeletons of the donor's `IslandSkeleton` (`Skeletons.tsx:7-
 * 18`), which stand beneath the header skeleton while the first page loads.
 */
export function SpecLinesIslandSkeleton() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((index) => (
        <SpecGroupSkeleton key={index} />
      ))}
    </>
  );
}
